import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Eye, X, AlertCircle, CheckCircle, Clock, Ban, User, Building, DollarSign, ChevronDown, ChevronUp, Check, XCircle, Pencil, Search } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import { formatIdentificationInput, formatIdentificationDisplay } from '../utils/formatIdentification';
import Pagination, { usePagination } from './Pagination';
import StyledDropdown from './StyledDropdown';

const InvestorManagement = ({ user }) => {
  const isAdmin = user?.role === 'Superadmin' || user?.role === 'Admin';

  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingInvestor, setViewingInvestor] = useState(null);
  const [expandedDividends, setExpandedDividends] = useState({});
  const [actionLoading, setActionLoading] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingInvestor, setRejectingInvestor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [investorSearch, setInvestorSearch] = useState('');
  const [setupLinkForCopy, setSetupLinkForCopy] = useState(null);
  const [setupLinkInView, setSetupLinkInView] = useState(null);
  const [loadingSetupLink, setLoadingSetupLink] = useState(false);
  const [regeneratingLink, setRegeneratingLink] = useState(false);
  const [setupLinkError, setSetupLinkError] = useState('');
  const [copiedInView, setCopiedInView] = useState(false);
  const [showRegenerateTooltip, setShowRegenerateTooltip] = useState(false);
  const [regenerateTooltipRect, setRegenerateTooltipRect] = useState(null);
  const regenerateBtnRef = useRef(null);
  const [editingView, setEditingView] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '', email: '', phone: '', icNumber: '',
    addressLine1: '', addressLine2: '', city: '', state: '', postcode: '', country: 'Malaysia',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    icNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Malaysia',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const listUrl = isAdmin ? `${getApiUrl()}/api/investors/all${params}` : `${getApiUrl()}/api/investors/my-investors${params}`;
      const investorsRes = await fetch(listUrl, { headers: getAuthHeaders() });
      const investorsResult = await investorsRes.json();
      if (investorsResult.success) setInvestors(investorsResult.data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterStatus, isAdmin]);

  useEffect(() => {
    if (!showViewModal) {
      setShowRegenerateTooltip(false);
      setRegenerateTooltipRect(null);
      setEditingView(false);
    }
  }, [showViewModal]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      icNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postcode: '',
      country: 'Malaysia',
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const response = await fetch(`${getApiUrl()}/api/investors`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        setSetupLinkForCopy(result._testSetupLink || null);
        setShowCreateModal(false);
        resetForm();
        fetchData();
        setTimeout(() => { setSuccess(''); setSetupLinkForCopy(null); }, 10000);
      } else {
        setError(result.message || 'Failed to create investor');
      }
    } catch (err) {
      console.error('Create investor error:', err);
      setError('Failed to create investor');
    }
  };

  const handleApprove = async (id) => {
    try {
      setActionLoading(id);
      setError('');
      const response = await fetch(`${getApiUrl()}/api/investors/${id}/approve`, {
        method: 'PUT',
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        setSetupLinkForCopy(result._testSetupLink || null);
        fetchData();
        setTimeout(() => { setSuccess(''); setSetupLinkForCopy(null); }, 10000);
      } else {
        setError(result.message || 'Failed to approve');
      }
    } catch (err) {
      console.error('Approve error:', err);
      setError('Failed to approve investor');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (investor) => {
    setRejectingInvestor(investor);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!rejectingInvestor) return;
    try {
      setActionLoading(rejectingInvestor.id);
      setError('');
      const response = await fetch(`${getApiUrl()}/api/investors/${rejectingInvestor.id}/reject`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: rejectReason }),
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(result.message);
        setShowRejectModal(false);
        setRejectingInvestor(null);
        fetchData();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setError(result.message || 'Failed to reject');
      }
    } catch (err) {
      console.error('Reject error:', err);
      setError('Failed to reject investor');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRegenerateSetupLink = async () => {
    if (!viewingInvestor?.id) return;
    setRegeneratingLink(true);
    setSetupLinkError('');
    try {
      const res = await fetch(`${getApiUrl()}/api/investors/regenerate-setup-link/${viewingInvestor.id}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const result = await res.json();
      if (res.ok && result.success && result.data?.setupLink) {
        setSetupLinkInView(result.data.setupLink);
        setSuccess(result.message || 'New link generated. Copy and send to the investor.');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        setSetupLinkError(result.message || 'Failed to regenerate link');
      }
    } catch (err) {
      console.error('Regenerate link error:', err);
      setSetupLinkError('Failed to regenerate link');
    } finally {
      setRegeneratingLink(false);
    }
  };

  const fetchSetupLink = async (investorId) => {
    setSetupLinkError('');
    setLoadingSetupLink(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/investors/setup-link/${investorId}`, { headers: getAuthHeaders() });
      let result = {};
      try {
        result = await res.json();
      } catch (_) {
        result = { message: res.statusText || `Request failed (${res.status})` };
      }
      if (res.ok && result.success && result.data?.setupLink) {
        setSetupLinkInView(result.data.setupLink);
      } else {
        setSetupLinkError(result.message || (res.ok ? 'Could not load link.' : `Request failed (${res.status}).`));
      }
    } catch (err) {
      console.error('Setup link fetch error:', err);
      setSetupLinkError('Network error. Check backend is running and try again.');
    } finally {
      setLoadingSetupLink(false);
    }
  };

  const startEditingView = () => {
    if (!viewingInvestor) return;
    setEditFormData({
      name: viewingInvestor.name || '',
      email: viewingInvestor.email || '',
      phone: viewingInvestor.phone || '',
      icNumber: viewingInvestor.icNumber || '',
      addressLine1: viewingInvestor.addressLine1 || '',
      addressLine2: viewingInvestor.addressLine2 || '',
      city: viewingInvestor.city || '',
      state: viewingInvestor.state || '',
      postcode: viewingInvestor.postcode || '',
      country: viewingInvestor.country || 'Malaysia',
    });
    setEditingView(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!viewingInvestor?.id) return;
    setSavingEdit(true);
    setError('');
    try {
      const res = await fetch(`${getApiUrl()}/api/investors/${viewingInvestor.id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData),
      });
      const result = await res.json();
      if (res.ok && result.success && result.data) {
        setViewingInvestor(result.data);
        setEditingView(false);
        setSuccess(result.message || 'Investor updated.');
        setTimeout(() => setSuccess(''), 5000);
        fetchData();
      } else {
        setError(result.message || 'Failed to update investor');
      }
    } catch (err) {
      console.error('Update investor error:', err);
      setError('Failed to update investor');
    } finally {
      setSavingEdit(false);
    }
  };

  const openViewModal = (investor) => {
    setViewingInvestor(investor);
    setSetupLinkInView(null);
    setSetupLinkError('');
    setCopiedInView(false);
    setEditingView(false);
    setError('');
    setShowViewModal(true);
    if (isAdmin && (investor.status === 'Approved' || investor.status === 'Active')) {
      fetchSetupLink(investor.id);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);

  const formatNumber = (value) => {
    if (value == null || value === '') return '';
    const num = parseFloat(String(value).replace(/,/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-MY').format(num);
  };

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' })
      : 'N/A';

  const getStatusBadge = (status) => {
    const styles = {
      Pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      Approved: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      Active: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      Rejected: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    };
    const Icon = status === 'Pending' ? Clock : status === 'Rejected' ? Ban : CheckCircle;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.Pending}`}>
        <Icon size={12} />
        {status}
      </span>
    );
  };

  const getDividendStatusBadge = (status) => {
    const styles = { Pending: 'bg-yellow-100 text-yellow-800', Paid: 'bg-green-100 text-green-800', Overdue: 'bg-red-100 text-red-800' };
    return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.Pending}`}>{status}</span>;
  };

  const toggleDividendExpand = (enrollmentId) => {
    setExpandedDividends((prev) => ({ ...prev, [enrollmentId]: !prev[enrollmentId] }));
  };

  const filteredInvestors = useMemo(() => {
    const q = investorSearch.trim().toLowerCase();
    const digitsQ = q.replace(/\D/g, '');
    if (!q) return investors;
    return investors.filter((inv) => {
      const ic = (inv.icNumber || '').replace(/\D/g, '');
      const parts = [
        inv.name,
        inv.email,
        inv.phone,
        inv.icNumber,
        inv.accountNumber,
        inv.status,
        inv.creator?.name,
        inv.addressLine1,
        inv.city,
        inv.state,
        inv.postcode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (parts.includes(q)) return true;
      if (digitsQ.length >= 3 && ic.includes(digitsQ)) return true;
      if (digitsQ && (inv.accountNumber || '').replace(/\D/g, '').includes(digitsQ)) return true;
      return false;
    });
  }, [investors, investorSearch]);

  const { currentPage, paginatedItems, totalItems, handlePageChange, resetPage } = usePagination(filteredInvestors, 10);
  useEffect(() => {
    resetPage();
  }, [filterStatus, investorSearch, resetPage]);

  const pendingCount = investors.filter((i) => i.status === 'Pending').length;

  if (loading && investors.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Investor Management</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">
            {isAdmin
              ? 'Create and manage investors (auto-approved) or approve/reject those created by agents. Add enrollments in Investor Enrollment.'
              : 'Create and manage investors. New investors require admin approval. Add enrollments in Investor Enrollment.'}
          </p>
        </div>
        {isAdmin && pendingCount > 0 && (
          <div className="flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-4 py-2 rounded-lg">
            <Clock size={20} />
            <span className="font-medium">{pendingCount} pending approval</span>
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus size={20} />
          New Investor
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle size={20} />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-2 text-green-700 dark:text-green-300">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} />
            {success}
          </div>
          {setupLinkForCopy && (
            <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800">
              <p className="text-xs font-medium text-green-800 dark:text-green-200 mb-1">Password setup link (copy and send to investor until email is configured):</p>
              <div className="flex gap-2">
                <input type="text" readOnly value={setupLinkForCopy} className="flex-1 text-xs bg-white dark:bg-slate-800 border border-green-300 dark:border-green-700 rounded px-2 py-1 text-gray-900 dark:text-dark-text" />
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(setupLinkForCopy); setSuccess('Link copied to clipboard.'); setTimeout(() => setSuccess(''), 2000); }}
                  className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                >
                  Copy
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 mb-6">
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
          <StyledDropdown
            options={[
              { value: '', label: 'All Status' },
              { value: 'Pending', label: 'Pending' },
              { value: 'Approved', label: 'Approved' },
              { value: 'Active', label: 'Active' },
              { value: 'Rejected', label: 'Rejected' },
            ]}
            value={filterStatus}
            onChange={setFilterStatus}
            placeholder="All Status"
            showClear
            className="min-w-[150px]"
          />
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-dark-muted pointer-events-none" />
            <input
              type="search"
              value={investorSearch}
              onChange={(e) => setInvestorSearch(e.target.value)}
              placeholder="Search name, email, IC, account, phone…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text placeholder:text-gray-400 dark:placeholder:text-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Search investors"
            />
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Email</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Enrollments</th>
                {isAdmin && (
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Created By</th>
                )}
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Status</th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted">
                    <User size={48} className="mx-auto mb-4 text-gray-300 dark:text-slate-600" />
                    <p className="text-lg font-medium">
                      {investors.length === 0 ? 'No investors found' : 'No investors match your filters'}
                    </p>
                    <p className="text-sm">
                      {investors.length === 0
                        ? 'Create your first investor to get started'
                        : 'Try adjusting search or status filter'}
                    </p>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-dark-text">{inv.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-muted">{inv.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900 dark:text-dark-text">
                      {inv.enrollments?.length ?? 0}
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-muted">{inv.creator?.name ?? 'N/A'}</td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-center">{getStatusBadge(inv.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button type="button" onClick={() => openViewModal(inv)} className="text-primary-600 hover:text-primary-800 p-1" title="View">
                          <Eye size={18} />
                        </button>
                        {isAdmin && inv.status === 'Pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprove(inv.id)}
                              disabled={actionLoading === inv.id}
                              className="text-green-600 hover:text-green-800 p-1 disabled:opacity-50"
                              title="Approve"
                            >
                              <Check size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openRejectModal(inv)}
                              disabled={actionLoading === inv.id}
                              className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50"
                              title="Reject"
                            >
                              <XCircle size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={10} onPageChange={handlePageChange} />
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">New Investor</h2>
              <button type="button" onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3 flex items-center gap-2">
                  <User size={18} />
                  Personal
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Identification Number *</label>
                    <input
                      type="text"
                      value={formData.icNumber}
                      onChange={(e) => setFormData({ ...formData, icNumber: formatIdentificationInput(e.target.value) })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      required
                    />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3 flex items-center gap-2">
                  <Building size={18} />
                  Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Address line 1</label>
                    <input
                      type="text"
                      value={formData.addressLine1}
                      onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Address line 2</label>
                    <input
                      type="text"
                      value={formData.addressLine2}
                      onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">State</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Postcode</label>
                    <input
                      type="text"
                      value={formData.postcode}
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Country</label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Malaysia"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t dark:border-dark-border">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                  Create Investor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingInvestor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Investor Details</h2>
              <div className="flex items-center gap-3">
                {getStatusBadge(viewingInvestor.status)}
                {isAdmin && !editingView && (
                  <button
                    type="button"
                    onClick={startEditingView}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"
                    title="Edit details"
                  >
                    <Pencil size={18} />
                    Edit
                  </button>
                )}
                <button type="button" onClick={() => setShowViewModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {editingView ? (
                <form onSubmit={handleSaveEdit} className="space-y-4">
                  {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
                  <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3">Personal</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Name *</label>
                        <input
                          type="text"
                          value={editFormData.name}
                          onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Email *</label>
                        <input
                          type="email"
                          value={editFormData.email}
                          onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Phone</label>
                        <input
                          type="tel"
                          value={editFormData.phone}
                          onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Identification Number *</label>
                        <input
                          type="text"
                          value={editFormData.icNumber}
                          onChange={(e) => setEditFormData({ ...editFormData, icNumber: formatIdentificationInput(e.target.value) })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Account number</label>
                        <input
                          type="text"
                          value={viewingInvestor?.accountNumber || 'N/A'}
                          readOnly
                          className="w-full border border-gray-200 dark:border-dark-border dark:bg-gray-100 dark:bg-slate-700/50 dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none cursor-default"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3">Address</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Address line 1</label>
                        <input
                          type="text"
                          value={editFormData.addressLine1}
                          onChange={(e) => setEditFormData({ ...editFormData, addressLine1: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Address line 2</label>
                        <input
                          type="text"
                          value={editFormData.addressLine2}
                          onChange={(e) => setEditFormData({ ...editFormData, addressLine2: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">City</label>
                        <input
                          type="text"
                          value={editFormData.city}
                          onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">State</label>
                        <input
                          type="text"
                          value={editFormData.state}
                          onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Postcode</label>
                        <input
                          type="text"
                          value={editFormData.postcode}
                          onChange={(e) => setEditFormData({ ...editFormData, postcode: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Country</label>
                        <input
                          type="text"
                          value={editFormData.country}
                          onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                          className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Malaysia"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button type="button" onClick={() => { setEditingView(false); setError(''); }} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                      Cancel
                    </button>
                    <button type="submit" disabled={savingEdit} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                      {savingEdit ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              ) : (
                <>
              {isAdmin && viewingInvestor.status === 'Pending' && (
                <div className="flex gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <button
                    type="button"
                    onClick={() => {
                      handleApprove(viewingInvestor.id);
                      setShowViewModal(false);
                    }}
                    disabled={actionLoading === viewingInvestor.id}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <Check size={18} />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowViewModal(false);
                      openRejectModal(viewingInvestor);
                    }}
                    disabled={actionLoading === viewingInvestor.id}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    <XCircle size={18} />
                    Reject
                  </button>
                </div>
              )}
              {isAdmin && (viewingInvestor.status === 'Approved' || viewingInvestor.status === 'Active') && (
                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                  <h3 className="font-medium text-primary-800 dark:text-primary-300 mb-2">Password setup link</h3>
                  <p className="text-xs text-primary-700 dark:text-primary-400 mb-2">Copy this link and send it to the investor so they can set their password. Link is valid for 24 hours.</p>
                  {loadingSetupLink ? (
                    <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent" />
                      Loading link...
                    </div>
                  ) : setupLinkInView ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={setupLinkInView}
                          className="flex-1 text-xs bg-white dark:bg-slate-800 border border-primary-300 dark:border-primary-700 rounded px-2 py-1.5 text-gray-900 dark:text-dark-text"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(setupLinkInView);
                            setCopiedInView(true);
                            setTimeout(() => setCopiedInView(false), 2000);
                          }}
                          className="px-3 py-1.5 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700 whitespace-nowrap"
                        >
                          {copiedInView ? 'Copied!' : 'Copy link'}
                        </button>
                        <span
                          ref={regenerateBtnRef}
                          className="relative inline-block cursor-not-allowed"
                          onMouseEnter={() => {
                            if (regenerateBtnRef.current) {
                              const rect = regenerateBtnRef.current.getBoundingClientRect();
                              setRegenerateTooltipRect(rect);
                              setShowRegenerateTooltip(true);
                            }
                          }}
                          onMouseLeave={() => {
                            setShowRegenerateTooltip(false);
                            setRegenerateTooltipRect(null);
                          }}
                        >
                          <button
                            type="button"
                            onClick={handleRegenerateSetupLink}
                            disabled={regeneratingLink || !!setupLinkInView}
                            className="px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {regeneratingLink ? '...' : 'Regenerate link'}
                          </button>
                          {showRegenerateTooltip && regenerateTooltipRect && createPortal(
                            <div
                              className="fixed z-[9999] px-3 py-2 text-xs text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg whitespace-normal min-w-[280px] max-w-[340px]"
                              style={{
                                left: regenerateTooltipRect.left,
                                top: regenerateTooltipRect.bottom + 6,
                              }}
                              role="tooltip"
                            >
                              A valid setup link already exists and has not been used. Use or share it before generating a new one to avoid creating too many links.
                            </div>,
                            document.body
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-dark-muted">
                        Forgot password? Ask your referral or account contact to send you a new setup link.
                      </p>
                    </div>
                  ) : (
                    <div>
                      {setupLinkError && (
                        <p className="text-sm text-red-600 dark:text-red-400 mb-2">{setupLinkError}</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={handleRegenerateSetupLink}
                          disabled={regeneratingLink}
                          className="px-3 py-1.5 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
                        >
                          {regeneratingLink ? 'Generating...' : 'Regenerate link'}
                        </button>
                        <button
                          type="button"
                          onClick={() => viewingInvestor?.id && fetchSetupLink(viewingInvestor.id)}
                          disabled={loadingSetupLink}
                          className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50"
                        >
                          Retry
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3">Personal</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><div className="text-gray-500 dark:text-dark-muted">Name</div><div className="font-medium dark:text-dark-text">{viewingInvestor.name}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Email</div><div className="font-medium dark:text-dark-text">{viewingInvestor.email}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Phone</div><div className="font-medium dark:text-dark-text">{viewingInvestor.phone || 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Identification Number</div><div className="font-medium dark:text-dark-text">{formatIdentificationDisplay(viewingInvestor.icNumber) || 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Account number</div><div className="font-medium dark:text-dark-text">{viewingInvestor.accountNumber || 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Created By</div><div className="font-medium dark:text-dark-text">{viewingInvestor.creator?.name ?? 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Created</div><div className="font-medium dark:text-dark-text">{formatDate(viewingInvestor.createdAt)}</div></div>
                  {viewingInvestor.approver && (
                    <>
                      <div><div className="text-gray-500 dark:text-dark-muted">Approved By</div><div className="font-medium dark:text-dark-text">{viewingInvestor.approver.name}</div></div>
                      <div><div className="text-gray-500 dark:text-dark-muted">Approved At</div><div className="font-medium dark:text-dark-text">{formatDate(viewingInvestor.approvedAt)}</div></div>
                    </>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 dark:text-dark-text mb-3">Address</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="md:col-span-2"><div className="text-gray-500 dark:text-dark-muted">Address line 1</div><div className="font-medium dark:text-dark-text">{viewingInvestor.addressLine1 || 'N/A'}</div></div>
                  <div className="md:col-span-2"><div className="text-gray-500 dark:text-dark-muted">Address line 2</div><div className="font-medium dark:text-dark-text">{viewingInvestor.addressLine2 || 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">City</div><div className="font-medium dark:text-dark-text">{viewingInvestor.city || 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">State</div><div className="font-medium dark:text-dark-text">{viewingInvestor.state || 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Postcode</div><div className="font-medium dark:text-dark-text">{viewingInvestor.postcode || 'N/A'}</div></div>
                  <div><div className="text-gray-500 dark:text-dark-muted">Country</div><div className="font-medium dark:text-dark-text">{viewingInvestor.country || 'N/A'}</div></div>
                </div>
              </div>
              {viewingInvestor.enrollments?.map((enr, idx) => (
                <div key={idx} className="border dark:border-dark-border rounded-lg overflow-hidden">
                  <div className="bg-primary-50 dark:bg-primary-900/20 p-4">
                    <h3 className="font-medium text-primary-800 dark:text-primary-300">
                      {enr.productDetails?.product?.name} — {enr.productDetails?.paymentMode}, {enr.productDetails?.tenureMonths} months
                    </h3>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div><div className="text-gray-500 dark:text-dark-muted">Amount</div><div className="font-medium text-lg dark:text-dark-text">{formatCurrency(enr.amount)}</div></div>
                      <div><div className="text-gray-500 dark:text-dark-muted">Interest</div><div className="font-medium dark:text-dark-text">{parseFloat(enr.productDetails?.interestRate || 0).toFixed(2)}% p.a.</div></div>
                      <div><div className="text-gray-500 dark:text-dark-muted">Start</div><div className="font-medium dark:text-dark-text">{formatDate(enr.startDate)}</div></div>
                      <div><div className="text-gray-500 dark:text-dark-muted">End</div><div className="font-medium dark:text-dark-text">{formatDate(enr.endDate)}</div></div>
                      <div><div className="text-gray-500 dark:text-dark-muted">Bank</div><div className="font-medium dark:text-dark-text">{enr.bankName}</div></div>
                      <div><div className="text-gray-500 dark:text-dark-muted">Account</div><div className="font-medium dark:text-dark-text">{enr.bankAccountNumber}</div></div>
                    </div>
                    <div className="border-t dark:border-dark-border pt-4">
                      <button type="button" onClick={() => toggleDividendExpand(enr.id)} className="flex items-center justify-between w-full text-left">
                        <span className="font-medium text-gray-900 dark:text-dark-text">Dividend Schedule ({enr.dividendPayments?.length || 0})</span>
                        {expandedDividends[enr.id] ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
                      </button>
                      {expandedDividends[enr.id] && enr.dividendPayments?.length > 0 && (
                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 dark:bg-slate-800/50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Year</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">#</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Due Date</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                              {enr.dividendPayments.map((d, i) => (
                                <tr key={i}>
                                  <td className="px-3 py-2 dark:text-dark-text">Year {d.year}</td>
                                  <td className="px-3 py-2 dark:text-dark-text">{d.dividendNumber}</td>
                                  <td className="px-3 py-2 dark:text-dark-text">{formatDate(d.dueDate)}</td>
                                  <td className="px-3 py-2 text-right dark:text-dark-text">{formatCurrency(d.amount)}</td>
                                  <td className="px-3 py-2 text-center">{getDividendStatusBadge(d.status)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setShowViewModal(false)} className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-dark-text rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600">
                Close
              </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && rejectingInvestor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">Reject Investor</h2>
              <button type="button" onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">This will permanently delete the investor and all related data.</p>
              </div>
              <p className="text-sm text-gray-600 dark:text-dark-muted">
                Investor: <strong>{rejectingInvestor.name}</strong> ({rejectingInvestor.email})
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Reason (optional)</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                  placeholder="Reason for rejection..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowRejectModal(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700">
                  Cancel
                </button>
                <button type="button" onClick={handleReject} disabled={actionLoading === rejectingInvestor.id} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {actionLoading === rejectingInvestor.id ? 'Rejecting...' : 'Reject & Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorManagement;
